import {
  isDefined,
  messageFromError,
  type NotificationReadAllInput,
  type NotificationReadOutput,
  type NotificationTopic,
} from '@terros/common'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useProfile } from '../user/get'
import { type UpdateHookResponse } from '../hooks'
import { useBackend, useServerRequest } from '../api'
import { readAllNotifications, type NotificationReadAllResult } from './readAll'

type ReadAllOperation = { request: NotificationReadAllInput; pending: boolean; error?: string }
export type ReadAllHookResponse = UpdateHookResponse<NotificationReadAllInput, NotificationReadAllResult> & {
  continuation?: NotificationReadAllInput
  ready: boolean
}

export function useMarkAllNotificationsRead(topic?: NotificationTopic): ReadAllHookResponse {
  const { user, company } = useProfile()
  const backend = useBackend()
  const client = useQueryClient()
  const request = useServerRequest<NotificationReadAllInput, NotificationReadOutput>('/notification/read')
  const ready = isDefined(user) && isDefined(company)
  const queryKey = [
    'notification-read-all',
    backend.getStage(),
    user?.userId,
    company?.companyId,
    backend.getImpersonating(),
    topic,
  ]
  // No initialData: the app's auth-context resetQueries clears even an observed pending/retry operation.
  const operation = useQuery<ReadAllOperation | null>({
    queryKey,
    queryFn: async () => null,
    enabled: false,
    gcTime: Infinity,
  })
  async function update(input: NotificationReadAllInput): Promise<NotificationReadAllResult> {
    if (!ready) throw new Error('Your account is still loading. Please retry.')
    const pending: ReadAllOperation = { request: input, pending: true }
    const saved = client.setQueryData(queryKey, pending)
    let continuation = input
    try {
      const result = await readAllNotifications(input, (page) => {
        if (client.getQueryData(queryKey) !== saved) throw new Error('Your account changed. Please retry.')
        continuation = page
        return request(page)
      })
      if (client.getQueryData(queryKey) === saved) client.setQueryData(queryKey, null)
      return result
    } catch (error) {
      // A reset while this request was in flight must not restore the previous account's operation.
      if (client.getQueryData(queryKey) === saved)
        client.setQueryData(queryKey, {
          request: continuation,
          pending: false,
          error: messageFromError(error),
        })
      throw error
    } finally {
      await client.invalidateQueries({ queryKey: ['notification-center'] })
    }
  }
  return {
    update,
    isUpdating: operation.data?.pending ?? false,
    error: operation.data?.error,
    continuation: operation.data?.request,
    ready,
  }
}
